import React from 'react'
import { levelData } from '../data/levelData'
import LevelNode from './LevelNode'

interface LevelPathProps {
  currentUserLevel: number
}

const LevelPath: React.FC<LevelPathProps> = ({ currentUserLevel }) => {
  // 決定是否顯示箭頭（基於響應式斷點和位置）
  const shouldShowArrow = (index: number, totalCount: number) => {
    // 最後一個等級永遠不顯示箭頭
    if (index === totalCount - 1) return false
    return true
  }

  // 獲取箭頭的響應式隱藏類別
  const getArrowVisibilityClass = (index: number) => {
    const classes = []
    
    // XL: 4列，隱藏第4,8,12...個
    if ((index + 1) % 4 === 0) {
      classes.push('xl:hidden')
    }
    
    // LG: 3列，隱藏第3,6,9...個
    if ((index + 1) % 3 === 0) {
      classes.push('lg:hidden xl:block')
    }
    
    // SM: 2列，隱藏第2,4,6...個
    if ((index + 1) % 2 === 0) {
      classes.push('sm:hidden lg:block')
    }
    
    // 小螢幕: 1列，隱藏所有箭頭
    classes.push('hidden sm:block')
    
    return classes.join(' ')
  }

  return (
    <div className="relative">
      {/* 等級路徑網格 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-12 lg:gap-16 max-w-7xl mx-auto">
        {levelData.map((data, index) => (
          <div
            key={data.level}
            className="relative flex justify-center"
          >
            <LevelNode
              data={data}
              isCurrentLevel={data.level === currentUserLevel}
            />
            
            {/* 響應式箭頭，依據不同螢幕尺寸控制顯示 */}
            {shouldShowArrow(index, levelData.length) && (
              <div className={`
                absolute top-1/2 -right-10 md:-right-12 transform -translate-y-1/2 
                transition-all duration-300 z-0
                ${data.level === currentUserLevel ? 'opacity-100 -right-12 md:-right-14' : 'opacity-70 hover:opacity-100 hover:-right-8 md:hover:-right-10'}
                ${getArrowVisibilityClass(index)}
              `}>
                <div className="flex items-center">
                  {/* 箭頭線條 */}
                  <div className={`
                    w-6 md:w-8 h-1 md:h-1.5 rounded-full
                    ${data.level === currentUserLevel ? 'bg-primary-500 shadow-lg shadow-primary-500/30' : 'bg-primary-400'}
                    ${data.level === currentUserLevel ? 'animate-pulse' : ''}
                    transition-all duration-300
                  `}></div>
                  
                  {/* 箭頭頭部 */}
                  <div className={`
                    w-0 h-0 ml-1
                    border-l-6 md:border-l-8 border-t-3 md:border-t-4 border-b-3 md:border-b-4
                    border-t-transparent border-b-transparent
                    ${data.level === currentUserLevel ? 'border-l-primary-500' : 'border-l-primary-400'}
                    ${data.level === currentUserLevel ? 'animate-pulse drop-shadow-lg' : ''}
                    transition-all duration-300
                  `}></div>
                </div>
                
                {/* 當前等級箭頭額外發光效果 */}
                {data.level === currentUserLevel && (
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-6 md:w-8 h-1 md:h-1.5 rounded-full bg-primary-400 opacity-30 animate-ping"></div>
                    <div className="w-0 h-0 ml-1 border-l-6 md:border-l-8 border-t-3 md:border-t-4 border-b-3 md:border-b-4 border-t-transparent border-b-transparent border-l-primary-400 opacity-30 animate-ping"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* 背景裝飾 - 連接線 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* 這裡可以加入背景連接線或其他裝飾元素 */}
        <svg
          className="absolute inset-0 w-full h-full opacity-10"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="grid"
              width="100"
              height="100"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 100 0 L 0 0 0 100"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                className="text-primary-300"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
    </div>
  )
}

export default LevelPath
